import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Lock, LockOpen, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { formatFecha, formatFechaHora } from '../../utils/format';

// ============================================================
// Cierre de mes por etapas (#111): Abierto → En revisión → Cerrado.
// En revisión cada punto de la lista se pone verde al corregirse o
// marcándolo "Revisado" con nota obligatoria; "Cerrar mes" (gerente o
// admin) queda apagado mientras haya algo en rojo. Cerrado bloquea todo lo
// que tenga fecha hasta fin de mes; solo gerente o admin lo reabre, con
// motivo. Las reglas viven en el servidor (cierrePeriodoController).
// ============================================================

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (mes) => `${MESES[Number(mes.slice(5, 7)) - 1]} ${mes.slice(0, 4)}`;

const IR = {
  sin_conciliar: '/conciliacion',
  sin_categoria: '/movimientos',
  sin_clasificar: '/contabilidad/configuracion',
  sin_tasa: '/tasas-cambio',
  sin_cierre_inventario: '/inventario/cierre',
  revision_ia: '#revision-ia' // bloque CierreIA, en esta misma página
};

const ESTADOS = {
  abierto: { texto: 'Abierto', clase: 'bg-slate-100 text-slate-600' },
  revision: { texto: 'En revisión', clase: 'bg-amber-50 text-amber-700' },
  cerrado: { texto: 'Cerrado', clase: 'bg-green-50 text-green-700' }
};

const btn = 'rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed';
const btnPrimario = `${btn} bg-primary-600 text-white hover:bg-primary-700`;
const btnSecundario = `${btn} border border-slate-300 text-slate-700 hover:bg-slate-50`;

function NotaEnLinea({ placeholder, textoBoton, onGuardar, onCancelar, ocupado }) {
  const [nota, setNota] = useState('');
  return (
    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-start">
      <textarea
        autoFocus
        rows={2}
        value={nota}
        onChange={(e) => setNota(e.target.value)}
        placeholder={placeholder}
        className="flex-1 rounded-md border px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <button type="button" className={btnPrimario} disabled={!nota.trim() || ocupado} onClick={() => onGuardar(nota.trim())}>{textoBoton}</button>
        <button type="button" className={btnSecundario} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

export default function EtapasCierre({ cierre, enCurso, rolActual, onCambio }) {
  const [editando, setEditando] = useState(null); // punto con la nota abierta, o 'reabrir'
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  const esContador = ['contador', 'gerente', 'admin'].includes(rolActual);
  const esGerente = ['gerente', 'admin'].includes(rolActual);
  const { mes, estado } = cierre;
  const base = `/reportes/cierre/${mes}`;
  const e = ESTADOS[estado];
  const enRevision = estado === 'revision';

  async function accion(fn) {
    setOcupado(true);
    setError('');
    try {
      await fn();
      setEditando(null);
      await onCambio();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo completar la acción');
    } finally {
      setOcupado(false);
    }
  }

  const rojos = cierre.puntos.filter((p) => !p.ok).length;
  const motivoNoCerrar = cierre.falta_anterior
    ? `Primero hay que cerrar ${nombreMes(cierre.falta_anterior)}.`
    : rojos > 0 ? `Quedan ${rojos === 1 ? '1 punto' : `${rojos} puntos`} en rojo: corrígelos o márcalos "Revisado" con una nota.` : '';

  return (
    <section className="bg-white rounded-xl shadow-sm overflow-hidden break-inside-avoid">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2">
          {estado === 'cerrado' ? <Lock size={16} className="text-green-600" /> : <LockOpen size={16} className="text-slate-400" />}
          Cierre de {nombreMes(mes)}
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${e.clase}`}>{e.texto}</span>
        </h2>
        <div className="flex flex-wrap gap-2 print:hidden">
          {estado === 'abierto' && !enCurso && !cierre.bloqueado_por_posterior && esContador && (
            <button type="button" className={btnPrimario} disabled={ocupado} onClick={() => accion(() => api.post(`${base}/revision`))}>
              Empezar cierre
            </button>
          )}
          {enRevision && esGerente && (
            <button
              type="button"
              className={btnPrimario}
              disabled={ocupado || !!motivoNoCerrar}
              title={motivoNoCerrar || undefined}
              onClick={() => accion(() => api.post(`${base}/cerrar`))}
            >
              Cerrar mes
            </button>
          )}
          {estado === 'cerrado' && cierre.es_ultimo_cerrado && esGerente && editando !== 'reabrir' && (
            <button type="button" className={btnSecundario} onClick={() => setEditando('reabrir')}>Reabrir mes</button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-1 text-sm text-slate-600">
        {estado === 'abierto' && enCurso && <p>El cierre se empieza cuando termine el mes.</p>}
        {estado === 'abierto' && !enCurso && !cierre.bloqueado_por_posterior && (
          <p>Al empezar el cierre se revisa esta lista; nada se bloquea hasta "Cerrar mes".</p>
        )}
        {cierre.bloqueado_por_posterior && estado !== 'cerrado' && (
          <p>Bloqueado: ya se cerró un mes posterior ({nombreMes(cierre.ultimo_cerrado)}).</p>
        )}
        {cierre.revision_por && <p>Cierre empezado por {cierre.revision_por} el {formatFechaHora(cierre.revision_en)}.</p>}
        {cierre.reabierto_por && (
          <p>Reabierto por {cierre.reabierto_por} el {formatFechaHora(cierre.reabierto_en)}. Motivo: {cierre.motivo_reapertura}</p>
        )}
        {estado === 'cerrado' && (
          <p className="text-green-700">
            Cerrado por {cierre.cerrado_por} el {formatFechaHora(cierre.cerrado_en)}. Nada con fecha hasta el {formatFecha(`${mes}-${new Date(Date.UTC(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0)).getUTCDate()}`)} se puede cargar, editar ni borrar.
          </p>
        )}
        {estado === 'cerrado' && !cierre.es_ultimo_cerrado && esGerente && (
          <p>Para reabrirlo, reabre primero los meses posteriores.</p>
        )}
        {enRevision && esGerente && motivoNoCerrar && <p className="text-amber-700">{motivoNoCerrar}</p>}
        {enRevision && !esGerente && <p>Cerrar el mes lo hace un gerente o admin.</p>}
        {editando === 'reabrir' && (
          <NotaEnLinea
            placeholder="Motivo para reabrir el mes (queda en Auditoría)"
            textoBoton="Reabrir"
            ocupado={ocupado}
            onCancelar={() => setEditando(null)}
            onGuardar={(motivo) => accion(() => api.post(`${base}/reabrir`, { motivo }))}
          />
        )}
        {error && <p className="text-red-600">{error}</p>}
      </div>

      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {cierre.puntos.map((p) => {
          const cantidadTxt = p.falta || (p.key === 'sin_cierre_inventario' ? (p.cantidad ? 'Falta' : 'Hecho') : p.cantidad);
          return (
            <li key={p.key} className="px-4 py-2.5 text-sm">
              <div className="flex items-center gap-2">
                {p.ok
                  ? <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                  : <AlertTriangle size={16} className="text-red-500 shrink-0" />}
                <span className="flex-1 text-slate-700">{p.texto}</span>
                <span className={`tabular-nums text-xs px-2 py-0.5 rounded-full ${p.cantidad ? 'bg-slate-100 text-slate-600' : 'text-slate-400'}`}>{cantidadTxt}</span>
                {p.cantidad > 0 && estado !== 'cerrado' && (IR[p.key].startsWith('#') ? (
                  <a href={IR[p.key]} className="inline-flex items-center gap-0.5 text-primary-600 hover:text-primary-700 print:hidden">
                    {p.falta ? 'Revisar' : 'Corregir'} <ChevronRight size={14} />
                  </a>
                ) : (
                  <Link to={IR[p.key]} className="inline-flex items-center gap-0.5 text-primary-600 hover:text-primary-700 print:hidden">
                    Corregir <ChevronRight size={14} />
                  </Link>
                ))}
                {enRevision && esContador && !p.ok && editando !== p.key && (
                  <button type="button" onClick={() => setEditando(p.key)} className="text-slate-500 hover:text-slate-700 underline print:hidden">
                    Marcar revisado
                  </button>
                )}
              </div>
              {p.revisado && (
                <p className="ml-6 mt-1 text-xs text-slate-500">
                  Revisado por {p.revisado.usuario_nombre} el {formatFechaHora(p.revisado.creado_en)}: {p.revisado.nota}
                  {enRevision && esContador && (
                    <button
                      type="button"
                      className="ml-2 underline hover:text-slate-700 print:hidden"
                      disabled={ocupado}
                      onClick={() => accion(() => api.delete(`${base}/revisado/${p.key}`))}
                    >
                      Quitar
                    </button>
                  )}
                </p>
              )}
              {p.revisado_vencido && (
                <p className="ml-6 mt-1 text-xs text-red-600">Aparecieron casos nuevos después de marcarlo revisado: hay que revisarlo otra vez.</p>
              )}
              {editando === p.key && (
                <div className="ml-6">
                  <NotaEnLinea
                    placeholder="Por qué queda así (obligatorio)"
                    textoBoton="Marcar revisado"
                    ocupado={ocupado}
                    onCancelar={() => setEditando(null)}
                    onGuardar={(nota) => accion(() => api.put(`${base}/revisado/${p.key}`, { nota }))}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
